import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ProjectService } from 'src/app/shared/project/project.service';
import { ControlColumnSelectComponent } from './column-types/control/control-column-selection.component';
import { BaseImportStepComponent, ImportStepState } from './base-import-step.component';
import { SeriesComponent } from './series/series.component';
import { OutcomeColumnSelectComponent } from './column-types/outcome/outcome-column-selection.component';
import { ImportStep } from './import-step';
import { ImportStepCompletionResult } from './import-step-completion-result';
import { FormulaInputComponent } from './column-types/outcome/formula-input.component';
import { DimensionsColumnsSelectComponent } from './column-types/dimensions/dimensions-columns-selection.component';
import { PreaggregationStrategySelectComponent } from './aggregation/preaggregation-strategy.component';
import { NewLostColumnSelectComponent } from './column-types/new-lost/new-lost-column-selection.component';
import { CalcOptionsComponent } from './column-types/reference/calc-options.component';
import { RelativeImpactOptionsComponent } from './column-types/reference/relative-impact-options.component';
import { DimensionSelectionResult } from './column-types/dimensions/dimensions-selection-result';
import { ForecastComponent } from './series/forecast.component';
import { AggregationSevice } from '../shared/preaggregation/aggregation-service';
import { DataService } from '../shared/network/data.service';

@Component({
  templateUrl: './import-wizard.component.html',
  styleUrls: ['./general.scss', './import-wizard.component.scss']
})
export class ImportWizardComponent implements OnInit {
    projectId: string | undefined;
    private currentStep: ImportStep | undefined = undefined;

    private destroy$ = new Subject<void>();
    
    @ViewChild('controlColumn') controlColumnComponent!: ControlColumnSelectComponent;
    @ViewChild('series') seriesComponent!: SeriesComponent;
    @ViewChild('outcomeColumn') outcomeColumnComponent!: OutcomeColumnSelectComponent;
    @ViewChild('formula') formulaInputComponent!: FormulaInputComponent;
    @ViewChild('newLost') newLostSelectionComponent!: NewLostColumnSelectComponent;
    @ViewChild('aggregationSettings') aggregationSettingsComponent!: PreaggregationStrategySelectComponent;
    @ViewChild('calcOptions') calcOptionsComponent!: CalcOptionsComponent;
    @ViewChild('relativeImpact') relativeImpactComponent!: RelativeImpactOptionsComponent;
    @ViewChild('dimensions') dimensionsColumnsComponent!: DimensionsColumnsSelectComponent;
    @ViewChild('preaggregationStrategy') preaggregationStrategyComponent!: PreaggregationStrategySelectComponent;
    @ViewChild('forecast') forecastComponent!: ForecastComponent;
    private allSteps: BaseImportStepComponent[] = [];

    public projectName: string = '';
    public downloadXlsxUrl: string = '';

    constructor(private activatedRoute: ActivatedRoute, private router: Router, private projectService: ProjectService, private aggregationSevice: AggregationSevice, private dataService: DataService) {
    }

    ngOnInit(): void {
        this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe(queryParams => {
            this.handleQueryParams(queryParams);
        });
    }
    
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    
    private async handleQueryParams(queryParams: any): Promise<void> {
        const projectId = queryParams.projectId;
        if (projectId) {
            this.projectId = projectId;
            this.projectService.setProjectId(projectId);
            this.downloadXlsxUrl =  this.dataService.getBaseUrl() + `/projects/downloadXlsx/${projectId}`;
            this.projectService.getProjectListItem(projectId).then(projItem => {
                this.projectName = projItem.name;
            });
        }
    
        const stepParam = queryParams.step;
        if (stepParam) {
            const chosenStep = ImportStep[stepParam as keyof typeof ImportStep];
            if (chosenStep !== undefined) {
                this.currentStep = chosenStep;
                const complexityDone: boolean = !!queryParams.complexityDone;
                this.activateStep(chosenStep);
                if (complexityDone) {
                    await this.onAfterComplexityDone();
                }
            }
        }
    }
    
    async ngAfterViewInit(): Promise<void> {
        const self = this;
        this.allSteps = [ 
            this.controlColumnComponent,
            this.seriesComponent,
            this.outcomeColumnComponent,
            this.formulaInputComponent,
            this.newLostSelectionComponent,
            this.aggregationSettingsComponent,
            this.calcOptionsComponent,
            this.relativeImpactComponent,
            this.dimensionsColumnsComponent,
            this.preaggregationStrategyComponent,
            this.forecastComponent,
        ];
        if (this.projectId) {
            this.allSteps.forEach(async (step) => {
                if (!step) {
                    return;
                }
                await step.loadData();
                step.setActive(step.step === self.currentStep);
            });
        }
    }

    gotoStep(newStep: ImportStep) {
        const currentParams = { ...this.activatedRoute.snapshot.queryParams };
        delete currentParams['complexityDone'];
        currentParams['step'] = ImportStep[newStep];

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: currentParams
        });
        
        // Scroll to the step
        this.scrollToStep(newStep);
    }

    public userClickedActivateStep(stepComp: BaseImportStepComponent) {
        if (stepComp.stepState > ImportStepState.NotAvailableYet) {
            this.gotoStep(stepComp.step);
        }
    }

    private activateStep(step: ImportStep) {
        for (let i = 0; i < this.allSteps.length; ++i) {
            if (this.allSteps[i]) {
                this.allSteps[i].setActive(this.allSteps[i].step === step);
            }
        }
    }

    private scrollToStep(step: ImportStep) {
        setTimeout(() => {
            const stepComponent = this.allSteps.find(s => s && s.step === step);
            if (stepComponent) {
                const element = this.getElementForStep(stepComponent);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }, 50);
    }

    private getElementForStep(stepComponent: BaseImportStepComponent): HTMLElement | null {
        if (stepComponent === this.controlColumnComponent) {
            return document.querySelector('control-column-selection');
        } else if (stepComponent === this.seriesComponent) {
            return document.querySelector('series');
        } else if (stepComponent === this.outcomeColumnComponent) {
            return document.querySelector('outcome-column-selection');
        } else if (stepComponent === this.formulaInputComponent) {
            return document.querySelector('formula-input-selection');
        } else if (stepComponent === this.newLostSelectionComponent) {
            return document.querySelector('new-lost-column-selection');
        } else if (stepComponent === this.aggregationSettingsComponent) {
            return document.querySelector('aggregation-settings');
        } else if (stepComponent === this.calcOptionsComponent) {
            return document.querySelector('calc-options');
        } else if (stepComponent === this.relativeImpactComponent) {
            return document.querySelector('relative-impact-options');
        } else if (stepComponent === this.dimensionsColumnsComponent) {
            return document.querySelector('dimensions-selection');
        } else if (stepComponent === this.preaggregationStrategyComponent) {
            return document.querySelector('preaggregation-strategy');
        } else if (stepComponent === this.forecastComponent) {
            return document.querySelector('forecast');
        }
        return null;
    }

    public async onControlColumnStepCompleted(result: ImportStepCompletionResult) {
        this.controlColumnComponent.setActive(false);
        if (result.changesApplied) {
            await Promise.all([
                this.seriesComponent.loadData(),
                this.outcomeColumnComponent.loadData()
            ]);
        }
        if (this.seriesComponent.stepState > ImportStepState.NotAvailableYet) {
            this.gotoStep(ImportStep.Series);
            this.outcomeColumnComponent.stepState = ImportStepState.NotAvailableYet;
        } else {
            this.gotoStep(ImportStep.OutcomeColumn);
        }
    }

    public async onSeriesStepCompleted(result: ImportStepCompletionResult) {
        this.seriesComponent.setActive(false);
        this.outcomeColumnComponent.stepState = ImportStepState.Available;
        this.gotoStep(ImportStep.OutcomeColumn);
    }

    public async onOutcomeColumnStepCompleted(result: ImportStepCompletionResult) {
        this.outcomeColumnComponent.setActive(false);
        await this.formulaInputComponent.loadData();
        await this.newLostSelectionComponent.loadData();
        if (result.payload.haveFormula) {
            this.gotoStep(ImportStep.Formula);
        } else {
            this.gotoStep(ImportStep.NewLost);
        }
    }

    public async onFormulaStepCompleted(result: ImportStepCompletionResult) {
        this.formulaInputComponent.setActive(false);
        await this.newLostSelectionComponent.loadData();
        this.gotoStep(ImportStep.NewLost);
    }

    public async onNewLostStepCompleted(result: ImportStepCompletionResult) {
        this.newLostSelectionComponent.setActive(false);
        await this.aggregationSettingsComponent.loadData();
        if (this.aggregationSettingsComponent.stepState === ImportStepState.NotAvailableForProject) {   //no formula
            await this.relativeImpactComponent.loadData();
            this.gotoStep(ImportStep.RelativeImpactOptions);
        } else {
            this.gotoStep(ImportStep.AggregationSettings);
        }
    }

    public async onAggregationsSettingsStepCompleted(result: ImportStepCompletionResult) {
        this.aggregationSettingsComponent.setActive(false);
        await this.calcOptionsComponent.loadData();
        this.gotoStep(ImportStep.CalcOptions);
    }

    public async onCalcOptionsStepCompleted(result: ImportStepCompletionResult) {
        this.calcOptionsComponent.setActive(false);
        await this.relativeImpactComponent.loadData();
        this.gotoStep(ImportStep.RelativeImpactOptions);
    }

    public async onRelativeImpactStepCompleted(result: ImportStepCompletionResult) {
        this.relativeImpactComponent.setActive(false);
        await this.dimensionsColumnsComponent.loadData();
        this.gotoStep(ImportStep.Dimensions);
    }

    public async onDimensionsStepCompleted(result: ImportStepCompletionResult) {
        this.dimensionsColumnsComponent.setActive(false);
        const dimensionSelectionResult: DimensionSelectionResult  = result.payload;
        const complexityEstimationRes = dimensionSelectionResult.complexityEstimationResult
        if (complexityEstimationRes && complexityEstimationRes.showToUser) {
            this.router.navigateByUrl('/complexity-mgnt?projectId=' + this.projectId);
        } else {
            this.dimensionsDoneNoComplexityNeeded(dimensionSelectionResult.requirePreaggregation);
        }
    }

    private async onAfterComplexityDone(): Promise<void> {
        const requiresPreaggr = await this.aggregationSevice.requiresPreaggregation(this.projectId);
        await this.dimensionsDoneNoComplexityNeeded(requiresPreaggr);
    }
    
    private async dimensionsDoneNoComplexityNeeded(requirePreaggregation: boolean): Promise<void> {
        if (requirePreaggregation) {
            await this.preaggregationStrategyComponent.loadData();
            this.gotoStep(ImportStep.PreaggregationStrategy);
        } else {
            this.preaggregationStrategyComponent.stepState = ImportStepState.NotAvailableForProject;
            await this.forecastComponent.loadData();

            if (this.forecastComponent.stepState >= ImportStepState.Available) {
                this.gotoStep(ImportStep.Forecast);
            } else {
                this.router.navigateByUrl(`cards?projectId=${this.projectId}`);
            }
        }
    }

    public async onPreaggregationStrategyStepCompleted(result: ImportStepCompletionResult) {
        this.preaggregationStrategyComponent.setActive(false);
        await this.forecastComponent.loadData();
        if (this.forecastComponent.stepState >= ImportStepState.Available) {
            this.gotoStep(ImportStep.Forecast);
        } else {
            this.router.navigateByUrl('cards?projectId=' + this.projectId);
        }
    }

    public async onForecastStepCompleted(result: ImportStepCompletionResult) {
        this.router.navigateByUrl('cards?projectId=' + this.projectId);
    }

    public async deleteProject() {
        await this.projectService.deleteProject(this.projectId);
        this.router.navigateByUrl('/projects');
    }

}
